import * as React from "react";
import { API_Search, UserMinimal } from "../../../src/apidefs";
import { RLink } from "./RLink";
import { Entry } from "../models/entries";
import { TestIds } from "../testIds";

export type AutocompletePaneProps = {
  onClickUser?: (user: UserMinimal) => void;

  results: API_Search;
  entries?: boolean;
  users?: boolean;
  paneHeight: "short" | "tall";
  query: string;
};

const authorNames = (entry: EntryJSONWithUsers): string[] => {
  const names = [
    entry.username_0,
    entry.username_1,
    entry.username_2,
    entry.username_3,
    entry.username_4,
    entry.username_5,
  ].filter((x): x is string => Boolean(x));

  return names;
};

type EntryJSONWithUsers = ReturnType<typeof enhanceEntry>;

const enhanceEntry = (entry: Entry) =>
  entry as Entry & {
    username_0?: string;
    username_1?: string;
    username_2?: string;
    username_3?: string;
    username_4?: string;
    username_5?: string;
  };

const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) {
    return text;
  }

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "ig");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={`${part}-${index}`} className="text-chips-gold">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

export const AutocompletePane = (
  props: AutocompletePaneProps
): JSX.Element | null => {
  const { onClickUser, results, entries, users, paneHeight, query } = props;

  const panelHeightClass =
    paneHeight === "short" ? "max-h-64" : "max-h-[70vh] sm:max-h-[80vh]";

  if (results.entries.length === 0 && results.users.length === 0) {
    return (
      <div
        data-test="search-results"
        data-testid={TestIds.SearchResults}
        className={`flex flex-col items-center justify-center px-4 py-10 text-sm font-light text-slate-500 dark:text-white/70 ${panelHeightClass}`}
      >
        No results yet — try another search
      </div>
    );
  }

  return (
    <div
      data-test="search-results"
      data-testid={TestIds.SearchResults}
      className={`flex flex-col gap-4 overflow-y-auto px-1 pb-1 pt-2 text-sm text-slate-900 dark:text-white ${panelHeightClass}`}
    >
      {entries && results.entries.length > 0 && (
        <section className="space-y-2">
          <div className="px-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-white/50">
            Entries
          </div>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:divide-white/5 dark:border-white/10 dark:bg-white/5">
            {results.entries.map((entryJSON) => {
              const entry = enhanceEntry(new Entry(entryJSON));
              const authors = authorNames(entry);
              const subtitle = authors.length
                ? `by ${authors.join(", ")}`
                : "Anonymous entry";

              return (
                <RLink variant="unstyled" key={entry.id} to={entry.linkToEntry}>
                  <div className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-white/5">
                    <div className="flex-1">
                      <div className="text-base font-semibold text-slate-900 group-hover:text-chips-gold dark:text-white">
                        {highlightText(entry.title, query)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-white/60">
                        {highlightText(subtitle, query)}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">
                      {entry.type}
                    </div>
                  </div>
                </RLink>
              );
            })}
          </div>
        </section>
      )}

      {users && results.users.length > 0 && (
        <section className="space-y-2">
          <div className="px-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-white/50">
            Users
          </div>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:divide-white/5 dark:border-white/10 dark:bg-white/5">
            {results.users.map((user) => {
              const content = (
                <>
                  <img
                    src={
                      user.avatar_url || "https://chipscompo.com/img/avatar.png"
                    }
                    className="h-10 w-10 rounded-full object-cover"
                  ></img>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-slate-900 dark:text-white">
                      {highlightText(user.username, query)}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-white/60">
                      View profile
                    </div>
                  </div>
                </>
              );

              if (onClickUser) {
                return (
                  <button
                    key={user.username}
                    onClick={() => onClickUser(user)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-white/5"
                  >
                    {content}
                  </button>
                );
              }

              return (
                <RLink
                  key={user.username}
                  to={`/user/${user.username}`}
                  variant="unstyled"
                >
                  <div className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-white/5">
                    {content}
                  </div>
                </RLink>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};
